import { useState, useTransition } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { CharterApproval, ContactFrequency, ContactPurpose, DossierGroupMember, LoadingMeterConversion, PartyDetail } from "@/lib/types";
import { BackLink } from "@/components/card/BackLink";
import { useScreenLabel } from "@/lib/screenTrail";
import { Check, Field, ReadField, SaveBar, SectionTitle } from "@/components/card/CardParts";
import { listDossiers } from "@/features/dossiers/dossiers.api";
import { STATUS_CLASS, STATUS_LABEL_KEYS } from "@/features/dossiers/DossierDrawer";
import { ordersKeys } from "@/features/orders/orders.api";
import { dieselKeys, dieselMethodOptions } from "@/features/billing/diesel.api";
import { RequirementChips, RequirementPicker } from "@/features/orders/RequirementPicker";
import { ACCOUNT_KINDS, KIND_LABEL_KEYS, getAccount, palletAccountKeys, signed } from "@/features/palletAccounts/palletAccounts.api";
import { OrgClient } from "@/features/org/OrgClient";
import { listOrgNodes, listOrgUsers, orgKeys } from "@/features/org/org.api";
import {
  CONTACT_PURPOSES,
  LOADING_METER_CONVERSIONS,
  partiesKeys,
  saveCharterSettings,
  saveContactPoints,
  saveCustomerSettings,
  saveGeneralSettings,
  setOwnOrganization,
  setPartyApproval,
  type ContactPointInput,
} from "./parties.api";
import { RoleChip } from "./RoleChip";
import { CmrSenderField, type CmrSender } from "./CmrSenderField";
import { DossierGroupField } from "./DossierGroupField";
import { ParentField, type ParentChoice } from "./ParentField";
import { formatAddress } from "@/features/locations/labels";
import { PortalTab } from "./PortalTab";
import { listPartnerUsers, partnerUserKeys } from "./partnerUsers.api";
import { VAT_LABEL_KEYS, type VatCode } from "./vatCodes";
import { useSession, hasModule } from "@/lib/auth";
import { PartyOrdermails } from "@/features/intake/PartyOrdermails";

// The relation card (Organisaties → Relaties → one relation). One tab per role, so
// a relation that is customer and charter shows both, plus communication per
// message (docs/tms-stamdata.md §5). What AFAS holds is shown, not edited: a
// relation is changed in AFAS and reaches WLX through TALIS until the cutover
// (S23, S26). The fields WLX manages are set in the block "In WLX" (S28): for a
// customer the invoicing conversion, the fixed reference, self-billing and the fuel
// surcharge group; for a charter the planning board and the NVWA number. An admin
// marks which relation is our own organisation; that card then carries the
// organisation tree (S27).

export const PARTY_CARD_TABS = ["general", "customer", "charter", "communication", "portal", "ordermail", "dossiers", "organization"] as const;
export type PartyCardTab = (typeof PARTY_CARD_TABS)[number];

const TAB_LABEL_KEYS: Record<PartyCardTab, string> = {
  general: "parties.tabGeneral",
  customer: "parties.tabCustomer",
  charter: "parties.tabCharter",
  communication: "parties.tabCommunication",
  portal: "parties.tabPortal",
  ordermail: "parties.tabOrdermail",
  dossiers: "parties.tabDossiers",
  organization: "parties.tabOrganization",
};

const CONVERSION_LABEL_KEYS: Record<LoadingMeterConversion, string> = {
  weight_to_lm: "parties.conversionWeightToLm",
  lm_to_weight: "parties.conversionLmToWeight",
};

const APPROVAL_LABEL_KEYS: Record<CharterApproval, string> = {
  approved: "parties.approvalApproved",
  doubt: "parties.approvalDoubt",
  blocked: "parties.approvalBlocked",
};

const PURPOSE_LABEL_KEYS: Record<ContactPurpose, string> = {
  invoice: "parties.purposeInvoice",
  planning: "parties.purposePlanning",
  freight_documents: "parties.purposeFreightDocuments",
  order_confirmation: "parties.purposeOrderConfirmation",
  planned: "parties.purposePlanned",
  delivered: "parties.purposeDelivered",
  transport_order: "parties.purposeTransportOrder",
  warehouse_in: "parties.purposeWarehouseIn",
  warehouse_out: "parties.purposeWarehouseOut",
  warehouse_out_request: "parties.purposeWarehouseOutRequest",
  emballage_incomplete: "parties.purposeEmballageIncomplete",
  pre_advice: "parties.purposePreAdvice",
};

const SYSTEM_LABEL_KEYS: Record<string, string> = {
  afas: "parties.systemAfas",
  talis: "parties.systemTalis",
  tms: "parties.systemTms",
  wms: "parties.systemWms",
  erp: "parties.systemErp",
};

// The NVWA's public list of registered and recognised companies; there is no API to ask it (S28).
const NVWA_OVERVIEW_URL =
  "https://www.nvwa.nl/onderwerpen/zaken-regelen/erkenningen-registraties-en-vergunningen/overzicht-bedrijven-met-erkenningen-registraties-en-vergunningen";

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
}

// Saving a setting: the saved card goes straight into the cache; the lists are
// marked stale and refresh when they are next shown.
function useCardSave(partyId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function save(action: () => Promise<PartyDetail>) {
    setError(null);
    setSaved(false);
    startTransition(async () => {
      try {
        const next = await action();
        queryClient.setQueryData(partiesKeys.detail(partyId), next);
        void queryClient.invalidateQueries({ queryKey: partiesKeys.all, refetchType: "none" });
        setSaved(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return { isPending, error, saved, save };
}

// Approving lets the orders that waited for it through (the API rechecks them) and changes
// the number in the menu: both are asked again at once instead of on the next minute.
function useApprovalRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: partiesKeys.counts });
    void queryClient.invalidateQueries({ queryKey: ordersKeys.all });
  };
}

// ── Building blocks ───────────────────────────────────────────────────────────

function SourceNote() {
  const { t } = useTranslation();
  return (
    <div className="wl-alert wl-alert--info" style={{ marginBottom: "16px" }}>
      <span>{t("parties.readOnlyNote")}</span>
    </div>
  );
}

function EmptyRole({ text }: { text: string }) {
  return (
    <div className="wl-card" style={{ padding: "32px 24px", textAlign: "center" }}>
      <p className="t-body-sm t-muted" style={{ margin: 0 }}>
        {text}
      </p>
    </div>
  );
}

function SettingsHeading({ help }: { help: string }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
        {t("parties.wlxSettingsTitle")}
      </div>
      <p className="wl-field__help" style={{ margin: "4px 0 12px" }}>
        {help}
      </p>
    </>
  );
}

/**
 * A relation from AFAS nobody approved yet (S54; Camiel, 22-09-2026: "moet deze niet gebruikt
 * kunnen worden tot er een vinkje goedgekeurd wordt gezet"). Above every tab until it is
 * approved; the tick itself stays on Algemeen, where it can also be taken back.
 */
function ApprovalBanner({ party, canApprove }: { party: PartyDetail; canApprove: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, save } = useCardSave(party.id);
  const refresh = useApprovalRefresh();
  return (
    <div className="wl-alert wl-alert--warning" style={{ marginBottom: "16px", alignItems: "center" }}>
      <span style={{ flex: 1 }}>
        <strong>{t("parties.pendingTitle")}</strong> {t("parties.pendingText")}
        {error && (
          <span role="alert" style={{ display: "block", marginTop: "4px", color: "var(--text-danger)" }}>
            {error}
          </span>
        )}
      </span>
      {canApprove ? (
        <button
          type="button"
          className="wl-btn wl-btn--secondary wl-btn--sm"
          disabled={isPending}
          onClick={() =>
            save(async () => {
              const next = await setPartyApproval(party.id, true);
              refresh();
              return next;
            })
          }
        >
          {t("parties.approveButton")}
        </button>
      ) : (
        <span className="t-caption">{t("parties.pendingNoRight")}</span>
      )}
    </div>
  );
}

// ── Algemeen ──────────────────────────────────────────────────────────────────

/**
 * What WLX keeps on a relation itself: the approval a relation from AFAS waits for (S54), the group it belongs to and
 * its owner in Scoretrace (AFAS does not have them; Camiel, 21-10-2026), and for an admin which relation is our own
 * organisation (S27). One save for all.
 */
function WlxGeneralForm({ party, canApprove, isAdmin }: { party: PartyDetail; canApprove: boolean; isAdmin: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(party.id);
  const refresh = useApprovalRefresh();
  const [approved, setApproved] = useState(party.approvedAt !== null);
  const [ownOrganization, setOwn] = useState(party.isOwnOrganization);
  const [parent, setParent] = useState<ParentChoice | null>(party.parent);
  const wmsBefore = party.identifiers.find((identifier) => identifier.system === "wms")?.value ?? "";
  const [wmsOwner, setWmsOwner] = useState(wmsBefore);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save(async () => {
          let next = party;
          if (canApprove && approved !== (party.approvedAt !== null)) {
            next = await setPartyApproval(party.id, approved);
            refresh();
          }
          if (isAdmin && ownOrganization !== party.isOwnOrganization) next = await setOwnOrganization(party.id, ownOrganization);
          const parentChanged = (parent?.id ?? null) !== (party.parent?.id ?? null);
          const wmsChanged = wmsOwner.trim() !== wmsBefore;
          if (canApprove && (parentChanged || wmsChanged)) {
            next = await saveGeneralSettings(party.id, {
              ...(parentChanged ? { parentId: parent?.id ?? null } : {}),
              ...(wmsChanged ? { wmsOwner: wmsOwner.trim() || null } : {}),
            });
          }
          return next;
        });
      }}
    >
      <div className="wl-divider" style={{ margin: "20px 0 16px" }} />
      <SettingsHeading help={t("parties.wlxSettingsHelp")} />
      <div className="wl-vstack" style={{ gap: "12px", alignItems: "stretch" }}>
        {canApprove && (
          <Check id="party-approved" label={t("parties.approvedLabel")} help={t("parties.approvedHelp")} checked={approved} onChange={setApproved} />
        )}
        {canApprove && (
          <div className="wl-grid-2">
            <ParentField partyId={party.id} value={parent} onChange={setParent} />
            <Field id="party-wmsOwner" label={t("parties.wmsOwnerLabel")} help={t("parties.wmsOwnerHelp")}>
              <input id="party-wmsOwner" className="wl-input t-mono" value={wmsOwner} maxLength={40} onChange={(e) => setWmsOwner(e.target.value)} />
            </Field>
          </div>
        )}
        {isAdmin && (
          <Check
            id="party-isOwnOrganization"
            label={t("parties.ownOrganizationLabel")}
            help={t("parties.ownOrganizationHelp")}
            checked={ownOrganization}
            onChange={setOwn}
          />
        )}
      </div>
      <SaveBar canEdit isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

function GeneralTab({ party, isAdmin, canApprove }: { party: PartyDetail; isAdmin: boolean; canApprove: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="wl-card" style={{ padding: "20px 24px" }}>
      <SourceNote />
      <div className="wl-grid-2">
        <ReadField label={t("parties.nameLabel")}>{party.name}</ReadField>
        <ReadField label={t("parties.shortNameLabel")}>{party.shortName}</ReadField>
        {/* As AFAS has it, a debtor number makes a sales relation and a creditor number a purchase relation (S24). */}
        <ReadField label={t("parties.relationLabel")} help={t("parties.relationHelp")}>
          {party.salesRelation && <span className="wl-chip">{t("parties.relationSales")}</span>}
          {party.purchaseRelation && <span className="wl-chip">{t("parties.relationPurchase")}</span>}
          {!party.salesRelation && !party.purchaseRelation && <span className="t-muted">{t("parties.relationNone")}</span>}
        </ReadField>
        <ReadField label={t("parties.cocLabel")} mono>
          {party.cocNumber}
        </ReadField>
        <ReadField label={t("parties.vatLabel")} mono>
          {party.vatNumber}
        </ReadField>
        <ReadField label={t("parties.phoneLabel")}>{party.phone}</ReadField>
        <ReadField label={t("parties.websiteLabel")} help={t("parties.contactHelp")}>
          {party.website}
        </ReadField>
      </div>

      <div className="wl-divider" style={{ margin: "20px 0 16px" }} />

      <div className="wl-grid-2">
        {/* Read-only on purpose: correlation runs on these values. */}
        <div>
          <div className="t-caption" style={{ marginBottom: "6px" }}>
            {t("parties.identifiersTitle")}
          </div>
          {party.identifiers.length === 0 ? (
            <div className="t-body-sm t-muted">{t("parties.identifiersEmpty")}</div>
          ) : (
            <div className="wl-vstack" style={{ gap: "6px" }}>
              {party.identifiers.map((identifier) => (
                <div key={`${identifier.system}:${identifier.value}`} className="wl-hstack" style={{ gap: "8px", alignItems: "center" }}>
                  <span className="wl-chip">
                    {SYSTEM_LABEL_KEYS[identifier.system] ? t(SYSTEM_LABEL_KEYS[identifier.system] as never) : identifier.system}
                  </span>
                  <span className="t-mono t-body-sm">{identifier.value}</span>
                </div>
              ))}
            </div>
          )}
          <span className="wl-field__help" style={{ display: "block", marginTop: "8px" }}>
            {t("parties.identifiersHelp")}
          </span>
        </div>
        <div>
          <div className="t-caption" style={{ marginBottom: "6px" }}>
            {t("common.record")}
          </div>
          <div className="t-body-sm">
            {t("common.created")}: {formatDate(party.createdAt)}
          </div>
          <div className="t-body-sm">
            {t("common.lastUpdated")}: {formatDate(party.updatedAt)}
          </div>
          <div className="t-body-sm">
            {party.approvedAt === null
              ? t("parties.approvalPending")
              : party.approvedBy
                ? t("parties.approvedBy", { date: formatDate(party.approvedAt), name: party.approvedBy })
                : t("parties.approvedOn", { date: formatDate(party.approvedAt) })}
          </div>
        </div>
      </div>

      {(canApprove || isAdmin) && (
        <WlxGeneralForm
          key={`${party.approvedAt ?? ""}:${party.isOwnOrganization}:${party.parent?.id ?? ""}:${party.identifiers.find((i) => i.system === "wms")?.value ?? ""}`}
          party={party}
          canApprove={canApprove}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

// ── Klant ─────────────────────────────────────────────────────────────────────

function CustomerSettingsForm({ party, canEdit }: { party: PartyDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(party.id);
  const account = party.customerAccount;
  const [kgPerLoadingMeter, setKgPerLoadingMeter] = useState(String(account?.kgPerLoadingMeter ?? 1750));
  const [conversion, setConversion] = useState<LoadingMeterConversion>(account?.loadingMeterConversion ?? "weight_to_lm");
  const [reference, setReference] = useState(account?.defaultInvoiceReference ?? "");
  const [selfBilling, setSelfBilling] = useState(account?.selfBilling ?? false);
  const [fuelGroup, setFuelGroup] = useState(account?.fuelSurchargeGroup ?? "");
  // What only TALIS knew of a customer, kept in WLX since the relations come from AFAS (Camiel, 21-10-2026).
  const [referenceRequired, setReferenceRequired] = useState(account?.referenceRequired ?? false);
  const [goodsProfile, setGoodsProfile] = useState(account?.defaultGoodsProfile ?? "");
  // How the diesel surcharge percentage is set (Commercie → Diesel, docs/tms-facturatie.md §14).
  const [dieselMethodId, setDieselMethodId] = useState(account?.dieselMethod?.id ?? "");
  const dieselOptions = useQuery({ queryKey: dieselKeys.options(), queryFn: dieselMethodOptions, enabled: canEdit });
  const dieselChoices = [
    ...(dieselOptions.data ?? []),
    ...(account?.dieselMethod && !(dieselOptions.data ?? []).some((m) => m.id === account.dieselMethod!.id) ? [account.dieselMethod] : []),
  ];
  // How often this customer is invoiced, and whether per reference (Commercie → Facturen, §18).
  const [rhythm, setRhythm] = useState(account?.invoiceRhythm ?? "monthly");
  const [split, setSplit] = useState(account?.invoiceSplitBy ?? "");
  const [invoiceLanguage, setInvoiceLanguage] = useState(account?.invoiceLanguage ?? "nl");
  // What goes with the invoice mail (F12y): the specification (csv) and the signed CMRs.
  const [invoiceSpecification, setInvoiceSpecification] = useState(account?.invoiceSpecification ?? false);
  const [invoiceAttachCmr, setInvoiceAttachCmr] = useState(account?.invoiceAttachCmr ?? false);
  // Its transport goes on the warehouse invoice (FC Nederland's Transportpendels, §27.6): no invoice per consignment.
  const [transportViaWarehouse, setTransportViaWarehouse] = useState(account?.transportViaWarehouse ?? false);
  // Until when this customer may send an order for the next loading day. Empty is no
  // agreement, and then nothing of theirs is ever held back for being late (20-09-2026).
  const [cutoff, setCutoff] = useState(account?.orderCutoffTime ?? "");
  // An unloading with a narrow window makes this customer's order critical (docs/tms-orderintake.md §3l): the hours.
  const [criticalWindow, setCriticalWindow] = useState(account?.criticalWindowHours ? String(account.criticalWindowHours) : "");
  // The transport requirements every new consignment of this customer starts with (S47).
  const [requirements, setRequirements] = useState<string[]>(party.defaultRequirements.map((link) => link.requirement.id));
  // The CMR of this customer (docs/tms-documenten.md §4.2): whether WLX prints it, box 1, a remark on every one.
  const [cmrPrint, setCmrPrint] = useState(account?.cmrPrint ?? true);
  const [cmrGoodsLines, setCmrGoodsLines] = useState(account?.cmrGoodsLines ?? false);
  const [cmrCombine, setCmrCombine] = useState(account?.cmrCombine ?? false);
  // The delivery note of the WMS behind the CMR, for a consignment that loads in Haps (CEDO, CSM).
  const [deliveryNotePrint, setDeliveryNotePrint] = useState(account?.deliveryNotePrint ?? false);
  const [cmrSender, setCmrSender] = useState<CmrSender | null>(() => {
    const sender = account?.cmrSenderLocation;
    return sender ? { id: sender.id, code: sender.code, name: sender.name, city: sender.city, address: formatAddress(sender) } : null;
  });
  const [cmrRemark, setCmrRemark] = useState(account?.cmrRemark ?? "");
  // The trip report (IFCSUM) of the trips that unload this customer's consignments (docs/tms-golive.md §6a).
  const [tripReport, setTripReport] = useState<"" | "before" | "after">(account?.tripReport ?? "");
  // One customer order, one dossier unless this customer reuses its references; its scans to Cargosnap (TaskCentre 680).
  const [dossierPerOrder, setDossierPerOrder] = useState(account?.dossierPerOrder ?? false);
  const [cargosnap, setCargosnap] = useState(account?.cargosnap ?? false);
  // The customers this one is one customer with: their orders with the same reference share one dossier.
  const [dossiersWith, setDossiersWith] = useState<DossierGroupMember[]>(account?.dossiersWith ?? []);
  // The customers whose pendelwerk goes with this one's: the planning drives their pendel orders back and forth on one trip.
  const [pendelWith, setPendelWith] = useState<DossierGroupMember[]>(account?.pendelWith ?? []);

  return (
    <form
      className="wl-card"
      style={{ padding: "20px 24px" }}
      onSubmit={(e) => {
        e.preventDefault();
        save(async () => {
          const next = await saveCustomerSettings(party.id, {
            kgPerLoadingMeter: numberOrNull(kgPerLoadingMeter) ?? 1750,
            loadingMeterConversion: conversion,
            defaultInvoiceReference: reference.trim() || null,
            selfBilling,
            fuelSurchargeGroup: fuelGroup.trim() || null,
            referenceRequired,
            defaultGoodsProfile: goodsProfile.trim() || null,
            dieselMethodId: dieselMethodId || null,
            invoiceRhythm: rhythm,
            invoiceSplitBy: split === "reference" ? "reference" : null,
            invoiceLanguage,
            invoiceSpecification,
            invoiceAttachCmr,
            transportViaWarehouse,
            orderCutoffTime: cutoff.trim() || null,
            criticalWindowHours: numberOrNull(criticalWindow),
            defaultRequirementIds: requirements,
            cmrPrint,
            cmrGoodsLines,
            cmrCombine,
            deliveryNotePrint,
            cmrSenderLocationId: cmrSender?.id ?? null,
            cmrRemark: cmrRemark.trim() || null,
            tripReport: tripReport || null,
            dossierPerOrder,
            cargosnap,
            dossiersWithIds: dossiersWith.map((member) => member.id),
            pendelWithIds: pendelWith.map((member) => member.id),
          });
          // One that joined brings the customers it was one customer with already.
          setDossiersWith(next.customerAccount?.dossiersWith ?? []);
          setPendelWith(next.customerAccount?.pendelWith ?? []);
          return next;
        });
      }}
    >
      <SettingsHeading help={t("parties.wlxManagedHelp")} />
      <div className="wl-grid-2">
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-referenceRequired"
            label={t("parties.referenceRequiredLabel")}
            help={t("parties.referenceRequiredHelp")}
            checked={referenceRequired}
            disabled={!canEdit}
            onChange={setReferenceRequired}
          />
        </div>
        <Field id="customer-defaultGoodsProfile" label={t("parties.goodsProfileLabel")}>
          <input
            id="customer-defaultGoodsProfile"
            className="wl-input"
            value={goodsProfile}
            maxLength={20}
            disabled={!canEdit}
            onChange={(e) => setGoodsProfile(e.target.value)}
          />
        </Field>
        <Field id="customer-defaultInvoiceReference" label={t("parties.defaultReferenceLabel")} help={t("parties.defaultReferenceHelp")}>
          <input
            id="customer-defaultInvoiceReference"
            className="wl-input"
            value={reference}
            maxLength={100}
            disabled={!canEdit}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
        <Field id="customer-fuelSurchargeGroup" label={t("parties.fuelGroupLabel")}>
          <input
            id="customer-fuelSurchargeGroup"
            className="wl-input"
            value={fuelGroup}
            maxLength={40}
            disabled={!canEdit}
            onChange={(e) => setFuelGroup(e.target.value)}
          />
        </Field>
        <Field id="customer-dieselMethod" label={t("parties.dieselMethodLabel")} help={t("parties.dieselMethodHelp")}>
          {canEdit ? (
            <select id="customer-dieselMethod" className="wl-select" value={dieselMethodId} onChange={(e) => setDieselMethodId(e.target.value)}>
              <option value="">{t("parties.dieselMethodNone")}</option>
              {dieselChoices.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.name}
                </option>
              ))}
            </select>
          ) : (
            <input id="customer-dieselMethod" className="wl-input" value={account?.dieselMethod?.name ?? t("parties.dieselMethodNone")} disabled />
          )}
        </Field>
        <Field id="customer-invoiceRhythm" label={t("parties.invoiceRhythmLabel")} help={t("parties.invoiceRhythmHelp")}>
          <select
            id="customer-invoiceRhythm"
            className="wl-select"
            value={rhythm}
            disabled={!canEdit}
            onChange={(e) => setRhythm(e.target.value as typeof rhythm)}
          >
            {(["per_order", "weekly", "monthly"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`invoices.rhythm.${value}` as never)}
              </option>
            ))}
          </select>
        </Field>
        <Field id="customer-invoiceSplit" label={t("parties.invoiceSplitLabel")}>
          <select
            id="customer-invoiceSplit"
            className="wl-select"
            value={split}
            disabled={!canEdit || rhythm === "per_order"}
            onChange={(e) => setSplit(e.target.value as typeof split)}
          >
            <option value="">{t("parties.invoiceSplitNone")}</option>
            <option value="reference">{t("parties.invoiceSplitReference")}</option>
          </select>
        </Field>
        <Field id="customer-invoiceLanguage" label={t("parties.invoiceLanguageLabel")} help={t("parties.invoiceLanguageHelp")}>
          <select
            id="customer-invoiceLanguage"
            className="wl-select"
            value={invoiceLanguage}
            disabled={!canEdit}
            onChange={(e) => setInvoiceLanguage(e.target.value as typeof invoiceLanguage)}
          >
            <option value="nl">{t("parties.invoiceLanguageNl")}</option>
            <option value="en">{t("parties.invoiceLanguageEn")}</option>
          </select>
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-invoiceSpecification"
            label={t("parties.invoiceSpecificationLabel")}
            help={t("parties.invoiceSpecificationHelp")}
            checked={invoiceSpecification}
            disabled={!canEdit}
            onChange={setInvoiceSpecification}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-invoiceAttachCmr"
            label={t("parties.invoiceAttachCmrLabel")}
            help={t("parties.invoiceAttachCmrHelp")}
            checked={invoiceAttachCmr}
            disabled={!canEdit}
            onChange={setInvoiceAttachCmr}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-transportViaWarehouse"
            label={t("parties.transportViaWarehouseLabel")}
            help={t("parties.transportViaWarehouseHelp")}
            checked={transportViaWarehouse}
            disabled={!canEdit}
            onChange={setTransportViaWarehouse}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-selfBilling"
            label={t("parties.selfBillingLabel")}
            help={t("parties.selfBillingHelp")}
            checked={selfBilling}
            disabled={!canEdit}
            onChange={setSelfBilling}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          {canEdit ? (
            <RequirementPicker
              id="customer-requirements"
              selected={requirements}
              onChange={setRequirements}
              label={t("parties.defaultRequirementsLabel")}
              help={t("parties.defaultRequirementsHelp")}
            />
          ) : (
            <Field id="customer-requirements" label={t("parties.defaultRequirementsLabel")} help={t("parties.defaultRequirementsHelp")}>
              <RequirementChips requirements={party.defaultRequirements.map((link) => link.requirement)} />
            </Field>
          )}
        </div>
        <Field id="customer-orderCutoffTime" label={t("parties.orderCutoffLabel")} help={t("parties.orderCutoffHelp")}>
          <input
            id="customer-orderCutoffTime"
            className="wl-input"
            type="time"
            step={60}
            style={{ width: "140px" }}
            value={cutoff}
            disabled={!canEdit}
            onChange={(e) => setCutoff(e.target.value)}
          />
        </Field>
        <Field id="customer-criticalWindowHours" label={t("parties.criticalWindowLabel")} help={t("parties.criticalWindowHelp")}>
          <input
            id="customer-criticalWindowHours"
            className="wl-input"
            type="number"
            min={1}
            max={23}
            style={{ width: "140px" }}
            placeholder={t("parties.criticalWindowPlaceholder")}
            value={criticalWindow}
            disabled={!canEdit}
            onChange={(e) => setCriticalWindow(e.target.value)}
          />
        </Field>
        <Field id="customer-kgPerLoadingMeter" label={t("parties.kgPerLmLabel")} help={t("parties.kgPerLmHelp")}>
          <input
            id="customer-kgPerLoadingMeter"
            className="wl-input"
            type="number"
            min={1}
            max={10000}
            step={1}
            required
            value={kgPerLoadingMeter}
            disabled={!canEdit}
            onChange={(e) => setKgPerLoadingMeter(e.target.value)}
          />
        </Field>
        <Field id="customer-loadingMeterConversion" label={t("parties.conversionLabel")} help={t("parties.conversionHelp")}>
          <select
            id="customer-loadingMeterConversion"
            className="wl-select"
            value={conversion}
            disabled={!canEdit}
            onChange={(e) => setConversion(e.target.value as LoadingMeterConversion)}
          >
            {LOADING_METER_CONVERSIONS.map((option) => (
              <option key={option} value={option}>
                {t(CONVERSION_LABEL_KEYS[option] as never)}
              </option>
            ))}
          </select>
        </Field>
        <SectionTitle>{t("parties.cmrTitle")}</SectionTitle>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-cmrPrint"
            label={t("parties.cmrPrintLabel")}
            help={t("parties.cmrPrintHelp")}
            checked={cmrPrint}
            disabled={!canEdit}
            onChange={setCmrPrint}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-cmrGoodsLines"
            label={t("parties.cmrGoodsLinesLabel")}
            help={t("parties.cmrGoodsLinesHelp")}
            checked={cmrGoodsLines}
            disabled={!canEdit || !cmrPrint}
            onChange={setCmrGoodsLines}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-cmrCombine"
            label={t("parties.cmrCombineLabel")}
            help={t("parties.cmrCombineHelp")}
            checked={cmrCombine}
            disabled={!canEdit || !cmrPrint}
            onChange={setCmrCombine}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-deliveryNotePrint"
            label={t("parties.deliveryNotePrintLabel")}
            help={t("parties.deliveryNotePrintHelp")}
            checked={deliveryNotePrint}
            disabled={!canEdit || !cmrPrint}
            onChange={setDeliveryNotePrint}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <CmrSenderField value={cmrSender} onChange={setCmrSender} disabled={!canEdit} />
        </div>
        <Field id="customer-cmrRemark" label={t("parties.cmrRemarkLabel")} help={t("parties.cmrRemarkHelp")} wide>
          <textarea
            id="customer-cmrRemark"
            className="wl-input"
            rows={2}
            value={cmrRemark}
            maxLength={300}
            disabled={!canEdit}
            onChange={(e) => setCmrRemark(e.target.value)}
          />
        </Field>
        <SectionTitle>{t("parties.tripReportTitle")}</SectionTitle>
        <Field id="customer-tripReport" label={t("parties.tripReportLabel")} help={t("parties.tripReportHelp")}>
          <select
            id="customer-tripReport"
            className="wl-select"
            value={tripReport}
            disabled={!canEdit}
            onChange={(e) => setTripReport(e.target.value as typeof tripReport)}
          >
            <option value="">{t("parties.tripReportNone")}</option>
            <option value="before">{t("parties.tripReportBefore")}</option>
            <option value="after">{t("parties.tripReportAfter")}</option>
          </select>
        </Field>
        <SectionTitle>{t("parties.dossiersTitle")}</SectionTitle>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-dossierPerOrder"
            label={t("parties.dossierPerOrderLabel")}
            help={t("parties.dossierPerOrderHelp")}
            checked={dossierPerOrder}
            disabled={!canEdit}
            onChange={setDossierPerOrder}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <DossierGroupField partyId={party.id} value={dossiersWith} onChange={setDossiersWith} disabled={!canEdit} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="customer-cargosnap"
            label={t("parties.cargosnapLabel")}
            help={t("parties.cargosnapHelp")}
            checked={cargosnap}
            disabled={!canEdit}
            onChange={setCargosnap}
          />
        </div>
      </div>
      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

function CustomerTab({ party, canEditSettings }: { party: PartyDetail; canEditSettings: boolean }) {
  const { t } = useTranslation();
  const account = party.customerAccount;
  if (!account) return <EmptyRole text={t("parties.customerEmpty")} />;
  const role = party.roles.find((r) => r.role === "customer");
  const yesNo = (value: boolean | undefined) => t(value ? "common.yes" : "common.no");
  const vat =
    account.vatCode && account.vatCode in VAT_LABEL_KEYS ? t(VAT_LABEL_KEYS[account.vatCode as VatCode] as never) : account.vatCode;

  return (
    <div className="wl-vstack" style={{ gap: "16px", alignItems: "stretch" }}>
      <div className="wl-card" style={{ padding: "20px 24px" }}>
        <SourceNote />
        <div className="wl-grid-2">
          <ReadField label={t("parties.debtorNumberLabel")} mono>
            {account.debtorNumber}
          </ReadField>
          <ReadField label={t("parties.roleActiveLabel")}>{yesNo(role?.isActive)}</ReadField>

          <SectionTitle>{t("parties.invoicingTitle")}</SectionTitle>
          <ReadField label={t("parties.paymentTermLabel")}>{account.paymentTermDays}</ReadField>
          <ReadField label={t("parties.vatCodeLabel")}>{vat}</ReadField>

          <SectionTitle>{t("parties.invoiceAddressTitle")}</SectionTitle>
          <ReadField label={t("parties.invoiceStreetLabel")} wide>
            {account.invoiceStreet}
          </ReadField>
          <ReadField label={t("parties.postalCodeLabel")}>{account.invoicePostalCode}</ReadField>
          <ReadField label={t("parties.cityLabel")}>{account.invoiceCity}</ReadField>
          <ReadField label={t("parties.countryLabel")}>{account.invoiceCountryCode}</ReadField>
        </div>
      </div>
      <CustomerSettingsForm party={party} canEdit={canEditSettings} />
    </div>
  );
}

// ── Charter ───────────────────────────────────────────────────────────────────

function CharterSettingsForm({ party, canEdit }: { party: PartyDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(party.id);
  const charter = party.subcontractor;
  const [onPlanningBoard, setOnPlanningBoard] = useState(charter?.onPlanningBoard ?? false);
  const [gmpFeed, setGmpFeed] = useState(charter?.certificates.some((certificate) => certificate.kind === "gmp_feed") ?? false);
  const [nvwaNumber, setNvwaNumber] = useState(charter?.certificates.find((certificate) => certificate.kind === "nvwa")?.number ?? "");
  // The assessment, which TALIS kept and AFAS does not (Camiel, 21-10-2026).
  const [approval, setApproval] = useState<CharterApproval | "">(charter?.approval ?? "");

  return (
    <form
      className="wl-card"
      style={{ padding: "20px 24px" }}
      onSubmit={(e) => {
        e.preventDefault();
        save(() => saveCharterSettings(party.id, { onPlanningBoard, gmpFeed, nvwaNumber: nvwaNumber.trim() || null, approval: approval || null }));
      }}
    >
      <SettingsHeading help={t("parties.wlxManagedHelp")} />
      <div className="wl-grid-2">
        <Field id="charter-approval" label={t("parties.approvalLabel")} help={t("parties.approvalHelp")}>
          <select
            id="charter-approval"
            className="wl-select"
            value={approval}
            disabled={!canEdit}
            onChange={(e) => setApproval(e.target.value as CharterApproval | "")}
          >
            <option value="">{t("parties.approvalNone")}</option>
            {(Object.keys(APPROVAL_LABEL_KEYS) as CharterApproval[]).map((value) => (
              <option key={value} value={value}>
                {t(APPROVAL_LABEL_KEYS[value] as never)}
              </option>
            ))}
          </select>
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check
            id="charter-onPlanningBoard"
            label={t("parties.onPlanningBoardLabel")}
            help={t("parties.onPlanningBoardHelp")}
            checked={onPlanningBoard}
            disabled={!canEdit}
            onChange={setOnPlanningBoard}
          />
        </div>

        <SectionTitle>{t("parties.certificatesTitle")}</SectionTitle>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check id="charter-gmpFeed" label={t("parties.certGmpFeed")} checked={gmpFeed} disabled={!canEdit} onChange={setGmpFeed} />
        </div>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="charter-nvwaNumber">
            {t("parties.certNvwaLabel")}
          </label>
          <input
            id="charter-nvwaNumber"
            className="wl-input t-mono"
            value={nvwaNumber}
            maxLength={40}
            disabled={!canEdit}
            onChange={(e) => setNvwaNumber(e.target.value)}
          />
          <span className="wl-field__help">
            {t("parties.certNvwaHelp")}{" "}
            <a href={NVWA_OVERVIEW_URL} target="_blank" rel="noopener noreferrer">
              {t("parties.nvwaLookup")}
            </a>
          </span>
        </div>
      </div>
      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

function CharterTab({ party, canEditSettings }: { party: PartyDetail; canEditSettings: boolean }) {
  const { t } = useTranslation();
  const charter = party.subcontractor;
  if (!charter) return <EmptyRole text={t("parties.charterEmpty")} />;
  const role = party.roles.find((r) => r.role === "charter");

  return (
    <div className="wl-vstack" style={{ gap: "16px", alignItems: "stretch" }}>
      <div className="wl-card" style={{ padding: "20px 24px" }}>
        <SourceNote />
        <div className="wl-grid-2">
          <ReadField label={t("parties.creditorNumberLabel")} mono>
            {charter.creditorNumber}
          </ReadField>
          <ReadField label={t("parties.roleActiveLabel")}>{t(role?.isActive ? "common.yes" : "common.no")}</ReadField>
        </div>
      </div>
      <CharterSettingsForm party={party} canEdit={canEditSettings} />
    </div>
  );
}

// ── Communicatie ──────────────────────────────────────────────────────────────
// The addresses per message are not in AFAS, so WLX manages them (S53; Camiel, 22-09-2026:
// "de email velden kunnen wijzigen, die komen niet uit afas"). TALIS fills them until the
// cutover; a message changed here is WLX's from then on, and TALIS leaves it alone.

/** Addresses as typed: split on commas, semicolons and spaces. */
function addressesOf(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean))];
}

const ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PointDraft {
  text: string;
  isActive: boolean;
  /** "" is not chosen yet: no done message goes (docs/tms-klantmails.md). */
  frequency: ContactFrequency | "";
  /** Only the inbound and outbound confirmation: wait for the scanned freight document. */
  waitForDocument: boolean;
}

const WAITS_FOR_DOCUMENT: readonly ContactPurpose[] = ["warehouse_in", "warehouse_out"];
// Goes to the mail address of the unloading place, not to the customer: only on or off (docs/tms-klantmails.md §7).
const TO_THE_PLACE: readonly ContactPurpose[] = ["pre_advice"];

function CommunicationTab({ party, canEdit }: { party: PartyDetail; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const { isPending, error, saved, save } = useCardSave(party.id);
  const dash = <span className="t-caption t-muted">—</span>;
  // Alphabetical by the name the reader sees, in either language.
  const purposes = CONTACT_PURPOSES.map((purpose) => ({ purpose, label: String(t(PURPOSE_LABEL_KEYS[purpose] as never)) })).sort((a, b) =>
    a.label.localeCompare(b.label, i18n.language),
  );
  const initial = (): Record<string, PointDraft> =>
    Object.fromEntries(
      CONTACT_PURPOSES.map((purpose) => {
        const point = party.contactPoints.find((p) => p.purpose === purpose);
        return [
          purpose,
          { text: point ? point.emails.join(", ") : "", isActive: point?.isActive ?? !TO_THE_PLACE.includes(purpose), frequency: point?.frequency ?? "", waitForDocument: point?.waitForDocument ?? false },
        ];
      }),
    );
  const [drafts, setDrafts] = useState(initial);
  const [invalid, setInvalid] = useState<string[]>([]);

  function change(purpose: string, patch: Partial<PointDraft>) {
    setDrafts((prev) => ({ ...prev, [purpose]: { ...prev[purpose]!, ...patch } }));
    setInvalid((prev) => prev.filter((other) => other !== purpose));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const before = initial();
    const changed: ContactPointInput[] = CONTACT_PURPOSES.filter((purpose) => {
      const now = drafts[purpose]!;
      const was = before[purpose]!;
      return (
        addressesOf(now.text).join(",") !== addressesOf(was.text).join(",") ||
        now.isActive !== was.isActive ||
        now.frequency !== was.frequency ||
        now.waitForDocument !== was.waitForDocument
      );
    }).map((purpose) => ({
      purpose,
      emails: addressesOf(drafts[purpose]!.text),
      isActive: drafts[purpose]!.isActive,
      ...(purpose === "delivered" ? { frequency: drafts[purpose]!.frequency || null } : {}),
      ...(WAITS_FOR_DOCUMENT.includes(purpose) ? { waitForDocument: drafts[purpose]!.waitForDocument } : {}),
    }));
    const wrong = changed.filter((point) => point.emails.some((address) => !ADDRESS.test(address))).map((point) => point.purpose);
    setInvalid(wrong);
    if (wrong.length > 0 || changed.length === 0) return;
    save(() => saveContactPoints(party.id, changed));
  }

  return (
    <form className="wl-card" style={{ padding: "20px 24px" }} onSubmit={submit}>
      <div className="wl-alert wl-alert--info" style={{ marginBottom: "16px" }}>
        <span>{t("parties.communicationNote")}</span>
      </div>
      <p className="wl-field__help" style={{ margin: "0 0 12px" }}>
        {t("parties.communicationHelp")}
      </p>
      <div className="wl-table-wrap">
        <table className="wl-table">
          <thead>
            <tr>
              <th>{t("parties.colPurpose")}</th>
              <th>{t("parties.colAddresses")}</th>
              <th>{t("parties.colActive")}</th>
              <th>{t("parties.colSource")}</th>
            </tr>
          </thead>
          <tbody>
            {purposes.map(({ purpose, label }) => {
              const point = party.contactPoints.find((p) => p.purpose === purpose);
              const draft = drafts[purpose]!;
              const wrong = invalid.includes(purpose);
              return (
                <tr key={purpose}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <label htmlFor={`contact-${purpose}`}>{label}</label>
                  </td>
                  <td style={{ width: "60%", paddingTop: "var(--space-1)", paddingBottom: "var(--space-1)" }}>
                    {TO_THE_PLACE.includes(purpose) ? (
                      <span className="t-body-sm t-muted">{t("parties.toThePlace")}</span>
                    ) : canEdit ? (
                      <>
                        <input
                          id={`contact-${purpose}`}
                          className="wl-input"
                          value={draft.text}
                          placeholder={t("parties.addressesPlaceholder")}
                          aria-invalid={wrong || undefined}
                          onChange={(e) => change(purpose, { text: e.target.value })}
                        />
                        {wrong && (
                          <span className="wl-field__help" style={{ color: "var(--text-danger)" }} role="alert">
                            {t("parties.addressesInvalid")}
                          </span>
                        )}
                        {purpose === "delivered" && (
                          <select
                            className="wl-select"
                            style={{ marginTop: "var(--space-1)" }}
                            value={draft.frequency}
                            aria-label={t("parties.frequencyLabel", { purpose: label })}
                            onChange={(e) => change(purpose, { frequency: e.target.value as ContactFrequency | "" })}
                          >
                            {draft.frequency === "" && <option value="">{t("parties.frequencyNone")}</option>}
                            <option value="per_event">{t("parties.frequencyPerEvent")}</option>
                            <option value="daily">{t("parties.frequencyDaily")}</option>
                          </select>
                        )}
                        {WAITS_FOR_DOCUMENT.includes(purpose) && (
                          <label className="wl-hstack t-body-sm" style={{ gap: "var(--space-2)", alignItems: "center", marginTop: "var(--space-1)" }}>
                            <input
                              type="checkbox"
                              checked={draft.waitForDocument}
                              aria-label={t("parties.waitForDocumentLabel", { purpose: label })}
                              onChange={(e) => change(purpose, { waitForDocument: e.target.checked })}
                            />
                            <span>{t("parties.waitForDocument")}</span>
                          </label>
                        )}
                      </>
                    ) : point && point.emails.length > 0 ? (
                      <>
                        {point.emails.join(", ")}
                        {purpose === "delivered" && (
                          <span className="t-caption t-muted">
                            {" · "}
                            {t(point.frequency === "daily" ? "parties.frequencyDaily" : point.frequency === "per_event" ? "parties.frequencyPerEvent" : "parties.frequencyNone")}
                          </span>
                        )}
                        {WAITS_FOR_DOCUMENT.includes(purpose) && point.waitForDocument && (
                          <span className="t-caption t-muted">
                            {" · "}
                            {t("parties.waitForDocument")}
                          </span>
                        )}
                      </>
                    ) : (
                      dash
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                      <input
                        type="checkbox"
                        checked={draft.isActive}
                        aria-label={t("parties.contactActiveLabel", { purpose: label })}
                        onChange={(e) => change(purpose, { isActive: e.target.checked })}
                      />
                    ) : point ? (
                      t(point.isActive ? "common.yes" : "common.no")
                    ) : (
                      dash
                    )}
                  </td>
                  <td>
                    {point ? (
                      <span className="wl-chip">{t(point.source === "talis" ? "parties.sourceTalis" : "parties.sourceWlx")}</span>
                    ) : (
                      dash
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SaveBar canEdit={canEdit} isPending={isPending} saved={saved} error={error} />
    </form>
  );
}

// ── Dossiers ──────────────────────────────────────────────────────────────────

function DossiersTab({ party }: { party: PartyDetail }) {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ["dossiers", "by-party", party.id], queryFn: () => listDossiers({ partyId: party.id }) });
  const items = query.data?.data ?? [];

  return (
    <div className="wl-table-wrap">
      <table className="wl-table">
        <thead>
          <tr>
            <th>{t("parties.colDossier")}</th>
            <th>{t("parties.colReference")}</th>
            <th>{t("common.status")}</th>
            <th>{t("parties.colUpdated")}</th>
          </tr>
        </thead>
        <tbody aria-busy={query.isLoading || undefined}>
          {!query.isLoading && items.length === 0 && (
            <tr>
              <td colSpan={4} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "24px" }}>
                {t("parties.dossiersEmpty")}
              </td>
            </tr>
          )}
          {items.map((dossier) => (
            <tr key={dossier.id}>
              <td className="mono">
                <Link to="/dossiers/$id" params={{ id: dossier.id }}>
                  {dossier.internalDossierNumber ?? "—"}
                </Link>
              </td>
              <td>{dossier.primaryReference}</td>
              <td>
                <span className={`wl-status ${STATUS_CLASS[dossier.status] ?? "wl-status--planned"}`}>
                  <span className="wl-status__dot" />
                  {STATUS_LABEL_KEYS[dossier.status] ? t(STATUS_LABEL_KEYS[dossier.status] as never) : dossier.status}
                </span>
              </td>
              <td className="mono">{formatDate(dossier.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Organisatie ───────────────────────────────────────────────────────────────
// The manager tree that feeds the escalation digest (docs/org-hierarchy.md). It
// used to be Beheer → Organisatie; it belongs to our own organisation (S27).

function OrganizationTab() {
  const nodesQuery = useQuery({ queryKey: orgKeys.list, queryFn: listOrgNodes });
  const usersQuery = useQuery({ queryKey: orgKeys.users, queryFn: listOrgUsers });
  return (
    <OrgClient
      nodes={nodesQuery.data ?? []}
      users={usersQuery.data ?? []}
      isFetching={nodesQuery.isFetching || usersQuery.isFetching}
      isLoading={nodesQuery.isLoading}
    />
  );
}

// ── Pallets ───────────────────────────────────────────────────────────────────
// The balance on the pallet account (docs/scantafel.md §5), when it has one: per role and kind, from Willems.

function PalletBalance({ partyId }: { partyId: string }) {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: palletAccountKeys.detail(partyId), queryFn: () => getAccount(partyId), retry: false });
  const account = query.data;
  if (!account || (account.mutations.length === 0 && !account.agreement.set)) return null;
  const roles = (["principal", "charter"] as const).filter((role) => role === "principal" || account.party.isCharter);
  return (
    <div className="wl-card" style={{ padding: "var(--space-4) var(--space-5)", marginBottom: "var(--space-4)" }}>
      <div className="wl-hstack" style={{ gap: "var(--space-6)", alignItems: "center", flexWrap: "wrap" }}>
        <span className="t-body-sm t-muted">{t("palletAccounts.onPartyCard")}</span>
        {roles.map((role) => (
          <span key={role} className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "baseline" }}>
            {roles.length > 1 && <span className="t-body-sm t-muted">{t(role === "principal" ? "palletAccounts.role.principal" : "palletAccounts.role.charter")}</span>}
            {ACCOUNT_KINDS.map((kind) => (
              <span key={kind} className="t-body">
                <span className="t-muted">{t(KIND_LABEL_KEYS[kind] as never)}</span> <span className="mono">{signed(account.balances[role][kind])}</span>
              </span>
            ))}
          </span>
        ))}
        {!account.agreement.exchanges && <span className="wl-flag wl-flag--muted">{t("palletAccounts.agreement.none")}</span>}
        <span className="wl-spacer" />
        <Link to="/pallet-accounts/$partyId" params={{ partyId }} className="t-body-sm">
          {t("palletAccounts.toAccount")}
        </Link>
      </div>
    </div>
  );
}

// ── The card ──────────────────────────────────────────────────────────────────

interface PartyCardProps {
  party: PartyDetail;
  /** parties_write: the fields WLX manages (S28). */
  canEditSettings: boolean;
  /** Marks our own organisation and keeps its organisation tree. */
  isAdmin: boolean;
  canSeeDossiers: boolean;
  /** partner_users: the people of this relation on the customer portal (docs/partner-portal.md). */
  canManagePortal: boolean;
  /** Whose column layouts the lists on the card keep. */
  userId: string;
  tab: PartyCardTab;
  onTab: (tab: PartyCardTab) => void;
  /** tms_orders, tms_orders_write or pallet_accounts_write: the balance on the pallet account (docs/scantafel.md §5). */
  canSeePallets?: boolean;
}

export function PartyCard({ party, canEditSettings, isAdmin, canSeeDossiers, canManagePortal, userId, tab, onTab, canSeePallets = false }: PartyCardProps) {
  const { t } = useTranslation();
  useScreenLabel(party.name);
  const session = useSession();
  // The ordermail (docs/invoerbak.md §3b): behind order_intake, as the invoerbak is.
  const canOrdermail = Boolean(session.data && (session.data.roles.includes("admin") || hasModule(session.data, "order_intake")));
  const tabs = PARTY_CARD_TABS.filter(
    (key) =>
      (key !== "dossiers" || canSeeDossiers) &&
      (key !== "ordermail" || (canOrdermail && !party.isOwnOrganization)) &&
      (key !== "organization" || (isAdmin && party.isOwnOrganization)) &&
      // Nobody of our own organisation signs in on the customer portal.
      (key !== "portal" || (canManagePortal && !party.isOwnOrganization)),
  );
  const active = tabs.includes(tab) ? tab : "general";
  // How many people of this relation are on the portal, on the tab (H-b01): the list the tab loads anyway.
  const portalUsers = useQuery({
    queryKey: partnerUserKeys.byParty(party.id),
    queryFn: () => listPartnerUsers(party.id),
    enabled: tabs.includes("portal"),
  });
  const portalCount = portalUsers.data?.users.length ?? 0;

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <BackLink list="/parties" listLabel={t("nav.parties")} style={{ marginBottom: "var(--space-2)" }} />
        <div className="wl-hstack" style={{ gap: "12px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
          <h1 className="t-h2" style={{ margin: 0 }}>
            {party.name}
          </h1>
          <span className={`wl-status ${party.isActive ? "wl-status--delivered" : "wl-status--cancelled"}`}>
            <span className="wl-status__dot" />
            {party.isActive ? t("common.active") : t("common.inactive")}
          </span>
          {party.approvedAt === null && (
            <span className="wl-status wl-status--onhold">
              <span className="wl-status__dot" />
              {t("parties.approvalPending")}
            </span>
          )}
          {party.isOwnOrganization && <span className="wl-chip wl-chip--navy">{t("parties.ownOrganizationLabel")}</span>}
          {party.roles.map((role) => (
            <RoleChip key={role.role} role={role} />
          ))}
        </div>
        <div className="wl-hstack t-body-sm t-muted" style={{ gap: "12px", flexWrap: "wrap" }}>
          <span className="t-mono">{party.code}</span>
          {party.shortName && (
            <>
              <span>·</span>
              <span>{party.shortName}</span>
            </>
          )}
          {party.parent && (
            <>
              <span>·</span>
              <span>
                {t("parties.parentLabel")}{" "}
                <Link to="/parties/$id" params={{ id: party.parent.id }}>
                  {party.parent.name}
                </Link>
              </span>
            </>
          )}
        </div>
      </div>

      {party.approvedAt === null && <ApprovalBanner party={party} canApprove={canEditSettings} />}

      <div className="wl-tabs" role="tablist" style={{ marginBottom: "16px" }}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            className={`wl-tab ${active === key ? "is-active" : ""}`}
            onClick={() => onTab(key)}
          >
            {t(TAB_LABEL_KEYS[key] as never)}
            {key === "portal" && portalCount > 0 && <span className="count">{portalCount}</span>}
          </button>
        ))}
      </div>

      {active === "general" && canSeePallets && <PalletBalance partyId={party.id} />}
      {active === "general" && <GeneralTab key={party.id} party={party} isAdmin={isAdmin} canApprove={canEditSettings} />}
      {active === "customer" && <CustomerTab key={party.id} party={party} canEditSettings={canEditSettings} />}
      {active === "charter" && <CharterTab key={party.id} party={party} canEditSettings={canEditSettings} />}
      {active === "communication" && <CommunicationTab key={party.id} party={party} canEdit={canEditSettings} />}
      {active === "portal" && canManagePortal && <PortalTab key={party.id} party={party} userId={userId} />}
      {active === "ordermail" && canOrdermail && <PartyOrdermails key={party.id} party={party} />}
      {active === "dossiers" && canSeeDossiers && <DossiersTab party={party} />}
      {active === "organization" && <OrganizationTab />}
    </>
  );
}
